<template>
  <div class="install-page">

    <div class="install-container">
      <!-- 页面标题 -->
      <header class="page-header">
        <div class="header-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          MCP 配置指南
        </div>
        <h1 class="page-title">客户端安装</h1>
        <p class="page-subtitle">快速配置 LayaAir MCP 服务，让 AI 更智能地辅助游戏开发</p>
      </header>

      <!-- Cursor 配置 -->
      <section class="config-section">
        <div class="section-header">
          <h2 class="section-title">Cursor</h2>
        </div>
        
        <div class="section-content">
          <p class="section-desc">在 Cursor 中按照以下路径添加 MCP 配置：</p>
          
          <div class="path-steps">
            <span class="path-item">Settings</span>
            <span class="path-arrow">→</span>
            <span class="path-item">Cursor Settings</span>
            <span class="path-arrow">→</span>
            <span class="path-item">Tool & MCP</span>
            <span class="path-arrow">→</span>
            <span class="path-item">Installed MCP Server</span>
            <span class="path-arrow">→</span>
            <span class="path-item highlight">Add Custom MCP</span>
          </div>

          <div class="code-wrapper">
            <div class="code-toolbar">
              <div class="code-dots">
                <span class="dot red"></span>
                <span class="dot yellow"></span>
                <span class="dot green"></span>
              </div>
              <span class="code-label">JSON</span>
              <button class="copy-btn" @click="copyToClipboard(cursorCode, 'cursor')">
                <svg v-if="!copyStatus.cursor" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ copyStatus.cursor ? '已复制' : '复制' }}
              </button>
            </div>
            <highlightjs language="json" :code="cursorCode"></highlightjs>
          </div>
        </div>
      </section>

      <!-- VS Code 配置 -->
      <section class="config-section">
        <div class="section-header">
          <h2 class="section-title">VS Code</h2>
        </div>
        
        <div class="section-content">
          <p class="section-desc">在 VS Code 的 MCP 配置里添加：</p>

          <div class="code-wrapper">
            <div class="code-toolbar">
              <div class="code-dots">
                <span class="dot red"></span>
                <span class="dot yellow"></span>
                <span class="dot green"></span>
              </div>
              <span class="code-label">JSON</span>
              <button class="copy-btn" @click="copyToClipboard(vsCodeCode, 'vscode')">
                <svg v-if="!copyStatus.vscode" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ copyStatus.vscode ? '已复制' : '复制' }}
              </button>
            </div>
            <highlightjs language="json" :code="vsCodeCode"></highlightjs>
          </div>
        </div>
      </section>

      <!-- Claude Code 配置 -->
      <section class="config-section">
        <div class="section-header">
          <h2 class="section-title">Claude Code</h2>
        </div>
        
        <div class="section-content">
          <p class="section-desc">在终端中执行以下命令：</p>

          <div class="code-wrapper">
            <div class="code-toolbar">
              <div class="code-dots">
                <span class="dot red"></span>
                <span class="dot yellow"></span>
                <span class="dot green"></span>
              </div>
              <span class="code-label">BASH</span>
              <button class="copy-btn" @click="copyToClipboard(claudeCode, 'claude')">
                <svg v-if="!copyStatus.claude" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ copyStatus.claude ? '已复制' : '复制' }}
              </button>
            </div>
            <highlightjs language="shell" :code="claudeCode"></highlightjs>
          </div>
        </div>
      </section>

      <!-- 配置说明 -->
      <section class="params-section">
        <div class="params-header">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          <h2>配置说明</h2>
        </div>

        <div class="params-grid">
          <!-- url -->
          <div class="param-card">
            <div class="param-name">url</div>
            <div class="param-desc">
              MCP 服务访问地址，用于连接 LayaAir 引擎相关的 MCP 知识库服务。
            </div>
          </div>

          <!-- LAYA_PRE_VERSION -->
          <div class="param-card featured">
            <div class="param-name">LAYA_PRE_VERSION</div>
            <div class="param-desc">
              用于<strong>版本差异对比查询</strong>的引擎版本号。
              <ul>
                <li>默认情况下，可与 <code>LAYA_VERSION</code> 保持一致</li>
                <li>当需要对比不同版本之间的变更时，可指定为旧版本号</li>
              </ul>
            </div>
            <div class="example-block">
              <div class="example-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
                示例说明
              </div>
              <div class="example-body">
                <p>假设当前项目基于 <code>v3.1.1</code> 开发，而引擎已升级至 <code>v3.3.5</code>，</p>
                <p>将 <code>LAYA_PRE_VERSION</code> 设置为 <code>v3.1.1</code>，<code>LAYA_VERSION</code> 设置为 <code>v3.3.5</code>，</p>
                <p>即可从知识库中获取期间的全部变更信息，包括但不限于：</p>
                
                <div class="version-compare">
                  <span class="ver old">v3.1.1</span>
                  <span class="arrow">→</span>
                  <span class="ver new">v3.3.5</span>
                </div>
                
                <div class="change-tags">
                  <span class="tag">类的新增/删除/修改</span>
                  <span class="tag">方法变更</span>
                  <span class="tag">方法参数调整</span>
                  <span class="tag">接口行为变化</span>
                </div>
              </div>
            </div>
          </div>

          <!-- LAYA_VERSION -->
          <div class="param-card">
            <div class="param-name">LAYA_VERSION</div>
            <div class="param-desc">
              当前 MCP 服务读取和工作的<strong>目标引擎版本标识</strong>。<br>
              该版本决定了 AI 查询和返回的内容基于哪个 LayaAir 引擎版本。
            </div>
          </div>

          <!-- LAYA_ALLOWED_DATASETS -->
          <div class="param-card">
            <div class="param-name">LAYA_ALLOWED_DATASETS</div>
            <div class="param-desc">
              允许访问的 MCP 知识库标识集合。
              <ul>
                <li>用于指定当前可用的 LayaAir 相关知识库</li>
                <li>后续将持续新增更多数据集，以辅助 AI 更智能地使用 LayaAir 引擎进行游戏开发</li>
              </ul>
            </div>
          </div>

          <!-- LAYA_MCP_API_KEY -->
          <div class="param-card">
            <div class="param-name">LAYA_MCP_API_KEY</div>
            <div class="param-desc">
              MCP 服务访问密钥，用于身份验证和授权访问。
            </div>
          </div>
        </div>
      </section>

      <!-- Cursor 规则文件 -->
      <section class="rules-section">
        <div class="rules-header">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          <h2>设置 Cursor 规则文件</h2>
        </div>

        <div class="rules-content">
          <p class="rules-desc">在 Cursor 中按照以下路径创建规则文件：</p>
          
          <div class="path-steps compact">
            <span class="path-item">Cursor Setting</span>
            <span class="path-arrow">→</span>
            <span class="path-item">Rules and Commands</span>
            <span class="path-arrow">→</span>
            <span class="path-item">Project Rules</span>
            <span class="path-arrow">→</span>
            <span class="path-item highlight">Add Rule</span>
          </div>

          <p class="rules-desc">
            将 <a class="file-link" :href="mdc" target="_blank">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              layaair.mdc
            </a> 里面的内容复制到你的规则文件里，也可以把 <code>layaair.mdc</code> 直接放到你的规则目录（项目 → <code>.cursor/rules</code>）
          </p>

          <div class="benefits-header">
            <div class="benefits-icon">✨</div>
            <h3>定义 layaair.mdc 规则文件的优点</h3>
          </div>

          <div class="benefits-grid">
            <div class="benefit-card">
              <div class="benefit-number">1</div>
              <div class="benefit-content">
                <h4>减少重复沟通，提升开发效率</h4>
                <p>无需在每次与 AI 交互时反复说明诸如「统一使用 <code>Laya.</code> 前缀」「本项目采用新版 UI 体系」等约定。规则文件一旦定义，即自动生效，开发者只需直接描述业务需求即可。</p>
              </div>
            </div>

            <div class="benefit-card">
              <div class="benefit-number">2</div>
              <div class="benefit-content">
                <h4>自动感知项目配置，精准使用 API</h4>
                <p>AI 会主动读取并解析 <code>PlayerSettings.json</code>，根据其中的 <code>addons</code>、<code>physics3dModule</code> 等配置项，自动判断当前项目所使用的功能模块和 API 版本，避免误用或混用接口。</p>
              </div>
            </div>

            <div class="benefit-card">
              <div class="benefit-number">3</div>
              <div class="benefit-content">
                <h4>统一代码风格，保障项目一致性</h4>
                <p>无论由谁使用 AI 生成代码，最终输出都遵循同一套规范，有效避免出现诸如部分文件使用 <code>Laya.Sprite</code>，部分文件直接使用 <code>Sprite</code> 等风格不统一的问题。</p>
              </div>
            </div>

            <div class="benefit-card">
              <div class="benefit-number">4</div>
              <div class="benefit-content">
                <h4>降低"凭空生成"风险，提升代码可靠性</h4>
                <p>规则中明确要求通过 MCP 查询官方 API 和知识库，AI 会优先基于真实文档进行推理和生成，而非依赖记忆或猜测，大幅降低生成错误或不存在接口的概率。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 页脚 -->
      <!-- <footer class="page-footer">
        <p>© 2024 LayaAir. All rights reserved. | <a href="https://layaair.com" target="_blank">官方网站</a></p>
      </footer> -->
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';

const mdc = ref(import.meta.env.VITE_MDC_URL)

const cursorCode = ref(`{
  "mcpServers": {
    "laya_mcp_server": {
      "url": "https://laya-knowledge-mcp.layaair.com/mcp",
      "headers": {
        "LAYA_PRE_VERSION" : "v3.3.5",
        "LAYA_VERSION" : "v3.3.5",
        "LAYA_ALLOWED_DATASETS": "LayaAir",
        "LAYA_MCP_API_KEY": "YOUR_API_KEY"
      }
    }
  }
}`)

const vsCodeCode = ref(`{
  "mcp": {
    "servers": {
      "laya_mcp_server": {
        "type": "http",
        "url": "https://laya-knowledge-mcp.layaair.com/mcp",
        "headers": {
          "LAYA_PRE_VERSION" : "v3.3.5",
          "LAYA_VERSION" : "v3.3.5",
          "LAYA_ALLOWED_DATASETS": "LayaAir",
          "LAYA_MCP_API_KEY": "YOUR_API_KEY"
        }
      }
    }
  }
}`)

const claudeCode = ref(`claude mcp add --transport http laya_mcp https://laya-knowledge-mcp.layaair.com/mcp \\
  --header "LAYA_PRE_VERSION: v3.3.5" \\
  --header "LAYA_VERSION: v3.3.5" \\
  --header "LAYA_ALLOWED_DATASETS: LayaAir" \\
  --header "LAYA_MCP_API_KEY: YOUR_API_KEY"`)

const copyStatus = reactive({
  cursor: false,
  vscode: false,
  claude: false
})

const copyToClipboard = async (code, type) => {
  try {
    await navigator.clipboard.writeText(code)
    copyStatus[type] = true
    setTimeout(() => {
      copyStatus[type] = false
    }, 2000)
  } catch (err) {
    console.error('复制失败:', err)
  }
}
</script>

<style lang="scss" scoped>
// 设计变量
$bg-dark: #0b0d10;
$bg-card: #12151a;
$bg-card-hover: #181c23;
$border-color: #2a2f38;
$border-glow: #c9a227;
$text-primary: #f5f5f7;
$text-secondary: #9ca3af;
$text-muted: #6b7280;
$accent-gold: #c9a227;
$accent-gold-light: #e6c84a;
$accent-gold-dark: #a8871f; // darken($accent-gold, 10%)
$accent-gold-darker: #8a6f1a; // darken($accent-gold, 15%)
$accent-blue: #3b82f6;
$accent-purple: #8b5cf6;
$accent-green: #10b981;
$accent-cyan: #06b6d4;

.install-page {
  position: relative;
  min-height: calc(100vh - 62px); // 减去 navbar 高度
  color: $text-primary;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Noto Sans SC', sans-serif;
  overflow-x: hidden;
}

// 背景效果
.bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.grid-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60%;
  background-image: 
    linear-gradient(rgba(201, 162, 39, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(201, 162, 39, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 100% 80% at 50% 0%, black 20%, transparent 80%);
  pointer-events: none;
  z-index: 0;
}

.install-container {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 48px 24px 64px;
}

// 页面头部
.page-header {
  text-align: center;
  margin-bottom: 64px;
  animation: fadeIn 0.6s ease-out;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba($accent-gold, 0.1);
  border: 1px solid rgba($accent-gold, 0.25);
  border-radius: 100px;
  font-size: 13px;
  font-weight: 500;
  color: $accent-gold;
  margin-bottom: 20px;
  
  svg {
    width: 16px;
    height: 16px;
  }
}

.page-title {
  font-size: 42px;
  font-weight: 700;
  background: linear-gradient(135deg, $accent-gold-light, $accent-gold, $accent-gold-dark);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 12px;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 16px;
  color: $text-secondary;
  margin: 0;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
}

// 配置区块
.config-section {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 16px;
  margin-bottom: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideUp 0.6s ease-out backwards;
  
  &:nth-child(2) { animation-delay: 0.1s; }
  &:nth-child(3) { animation-delay: 0.15s; }
  &:nth-child(4) { animation-delay: 0.2s; }
  
  &:hover {
    border-color: rgba($accent-gold, 0.3);
    box-shadow: 0 0 32px rgba($accent-gold, 0.05);
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid $border-color;
  background: rgba(0, 0, 0, 0.2);
}

.section-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &.cursor-icon {
    background: linear-gradient(135deg, #1a1a1a, #2d2d2d);
    color: #fff;
    border: 1px solid #404040;
  }
  
  &.vscode-icon {
    background: linear-gradient(135deg, #0078d4, #005ba1);
    color: #fff;
  }
  
  &.claude-icon {
    background: linear-gradient(135deg, #d4a574, #c08c5a);
    color: #fff;
  }
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: $text-primary;
  margin: 0;
}

.section-content {
  padding: 24px;
}

.section-desc {
  color: $text-secondary;
  font-size: 14px;
  margin: 0 0 16px;
  line-height: 1.6;
}

// 路径步骤
.path-steps {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  
  &.compact {
    margin-bottom: 24px;
  }
}

.path-item {
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid $border-color;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  color: $text-primary;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba($accent-gold, 0.08);
    border-color: rgba($accent-gold, 0.3);
  }
  
  &.highlight {
    background: rgba($accent-gold, 0.12);
    border-color: $accent-gold;
    color: $accent-gold;
  }
}

.path-arrow {
  color: $accent-gold;
  font-weight: 600;
  font-size: 14px;
}

// 代码块
.code-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid $border-color;
  background: #0a0c0f;
}

.code-toolbar {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid $border-color;
  gap: 12px;
}

.code-dots {
  display: flex;
  gap: 6px;
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    
    &.red { background: #ff5f56; }
    &.yellow { background: #ffbd2e; }
    &.green { background: #27ca40; }
  }
}

.code-label {
  font-size: 11px;
  font-family: 'SF Mono', monospace;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 1px;
  flex: 1;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba($accent-gold, 0.1);
  border: 1px solid rgba($accent-gold, 0.25);
  border-radius: 6px;
  color: $accent-gold;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    background: rgba($accent-gold, 0.2);
    border-color: $accent-gold;
  }
}

// 配置说明区块
.params-section {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  animation: slideUp 0.6s ease-out 0.25s backwards;
}

.params-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
  
  svg {
    width: 28px;
    height: 28px;
    color: $accent-gold;
  }
  
  h2 {
    font-size: 22px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }
}

.params-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.param-card {
  padding: 20px 24px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid $border-color;
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba($accent-gold, 0.3);
  }
  
  &.featured {
    border-color: rgba($accent-gold, 0.25);
    background: rgba($accent-gold, 0.03);
  }
}

.param-name {
  font-family: 'SF Mono', 'Monaco', monospace;
  font-size: 15px;
  font-weight: 600;
  color: $accent-gold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: $accent-gold;
    border-radius: 50%;
  }
}

.param-desc {
  color: $text-secondary;
  font-size: 14px;
  line-height: 1.7;
  
  strong {
    color: $text-primary;
  }
  
  code {
    background: rgba($accent-gold, 0.12);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'SF Mono', monospace;
    font-size: 13px;
    color: $accent-gold;
  }
  
  ul {
    margin: 12px 0 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 6px;
      
      &::marker {
        color: $accent-gold;
      }
    }
  }
}

// 示例区块
.example-block {
  margin-top: 16px;
  padding: 18px 20px;
  background: rgba($accent-gold, 0.05);
  border: 1px solid rgba($accent-gold, 0.15);
  border-radius: 10px;
  border-left: 3px solid $accent-gold;
}

.example-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: $accent-gold;
  margin-bottom: 14px;
  
  svg {
    width: 16px;
    height: 16px;
  }
}

.example-body {
  font-size: 14px;
  color: $text-secondary;
  line-height: 1.7;
  
  p {
    margin: 0 0 10px;
  }
  
  code {
    background: rgba($accent-gold, 0.12);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'SF Mono', monospace;
    font-size: 13px;
    color: $accent-gold;
  }
}

.version-compare {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  margin: 12px 0;
  
  .ver {
    font-family: 'SF Mono', monospace;
    font-size: 14px;
    font-weight: 600;
    
    &.old { color: $text-secondary; }
    &.new { color: $accent-cyan; }
  }
  
  .arrow {
    color: $accent-gold;
    font-weight: 700;
  }
}

.change-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  
  .tag {
    padding: 6px 12px;
    background: rgba(0, 0, 0, 0.25);
    border-radius: 6px;
    font-size: 12px;
    color: $text-secondary;
    
    &::before {
      content: '•';
      color: $accent-green;
      margin-right: 6px;
    }
  }
}

// 规则文件区块
.rules-section {
  background: linear-gradient(135deg, rgba($accent-purple, 0.08), rgba($bg-card, 0.9));
  border: 1px solid $border-color;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  animation: slideUp 0.6s ease-out 0.3s backwards;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 32px;
  border-bottom: 1px solid $border-color;
  background: rgba(0, 0, 0, 0.2);
  
  svg {
    width: 26px;
    height: 26px;
    color: $accent-purple;
  }
  
  h2 {
    font-size: 22px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }
}

.rules-content {
  padding: 28px 32px;
}

.rules-desc {
  color: $text-secondary;
  font-size: 14px;
  line-height: 1.7;
  margin: 0 0 16px;
  
  code {
    background: rgba($accent-gold, 0.12);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'SF Mono', monospace;
    font-size: 13px;
    color: $accent-gold;
  }
}

.file-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $accent-blue;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    color: $accent-gold;
  }
}

.benefits-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 32px 0 20px;
  
  .benefits-icon {
    font-size: 20px;
  }
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }
}

.benefits-grid {
  display: grid;
  gap: 14px;
}

.benefit-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid $border-color;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba($accent-gold, 0.3);
    transform: translateX(4px);
  }
}

.benefit-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $accent-gold, $accent-gold-darker);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  color: $bg-dark;
  flex-shrink: 0;
}

.benefit-content {
  flex: 1;
  
  h4 {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }
  
  p {
    font-size: 14px;
    color: $text-secondary;
    line-height: 1.7;
    margin: 0;
    
    code {
      background: rgba($accent-gold, 0.12);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'SF Mono', monospace;
      font-size: 12px;
      color: $accent-gold;
    }
  }
}

// 页脚
.page-footer {
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid $border-color;
  margin-top: 48px;
  animation: fadeIn 0.6s ease-out 0.4s backwards;
  
  p {
    color: $text-muted;
    font-size: 14px;
    margin: 0;
  }
  
  a {
    color: $accent-gold;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: $accent-gold-light;
    }
  }
}

// 动画
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 响应式
@media (max-width: 768px) {
  .install-container {
    padding: 32px 16px 48px;
  }
  
  .page-title {
    font-size: 32px;
  }
  
  .path-steps {
    flex-direction: column;
    align-items: flex-start;
    
    .path-arrow {
      transform: rotate(90deg);
      margin: 4px 0 4px 12px;
    }
  }
  
  .params-section,
  .rules-content {
    padding: 24px 20px;
  }
  
  .rules-header {
    padding: 20px;
  }
  
  .benefit-card {
    flex-direction: column;
    gap: 12px;
  }
}

// highlightjs 样式覆盖
:deep(.hljs) {
  padding: 20px !important;
  background: transparent !important;
  font-size: 13px;
  line-height: 1.7;
}
</style>
